/**
 * VersionContent - Display component for version details
 *
 * Shows the full content of a selected document version including
 * title, content, and metadata.
 */
import { format } from "date-fns";
import { FileText, Calendar, Hash } from "lucide-react";
import type { DocumentVersionDetail } from "../../../types/version";

interface VersionContentProps {
  /** Version details to display */
  version: DocumentVersionDetail | null;
  /** Whether content is loading */
  isLoading?: boolean;
  /** Error message if loading failed */
  error?: string | null;
}

export function VersionContent({
  version,
  isLoading = false,
  error = null,
}: VersionContentProps) {
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "MMMM d, yyyy 'at' h:mm:ss a");
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 p-6 bg-white">
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-1/4" />
            <div className="h-px bg-gray-200 my-4" />
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded w-5/6" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 p-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-lg bg-red-50 p-4 text-center">
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!version) {
    return (
      <div className="flex-1 p-6 bg-white flex items-center justify-center">
        <div className="text-center text-gray-500">
          <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">Select a version</p>
          <p className="text-sm text-gray-400 mt-1">
            Click on a version in the sidebar to view its content
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-6 bg-white overflow-y-auto">
      <div className="max-w-3xl mx-auto">
        {/* Version header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <Hash className="h-4 w-4" />
            <span>Version {version.version_number}</span>
            <span className="text-gray-300">•</span>
            <Calendar className="h-4 w-4" />
            <span>{formatDate(version.created_at)}</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{version.title}</h1>
        </div>

        {/* Divider */}
        <hr className="my-6 border-gray-200" />

        {/* Content */}
        <div className="prose prose-gray max-w-none">
          {version.formatted_content ? (
            <div
              className="ql-editor"
              dangerouslySetInnerHTML={{
                __html: renderQuillContent(version.formatted_content),
              }}
            />
          ) : (
            <div className="whitespace-pre-wrap text-gray-700">
              {version.content}
            </div>
          )}
        </div>

        {/* Metadata */}
        {version.meta && Object.keys(version.meta).length > 0 && (
          <>
            <hr className="my-6 border-gray-200" />
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Metadata</h3>
              <pre className="text-xs text-gray-600 overflow-x-auto">
                {JSON.stringify(version.meta, null, 2)}
              </pre>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Render Quill delta content to HTML
 * This is a simplified renderer - for full Quill rendering,
 * use the quill-delta-to-html library
 */
function renderQuillContent(content: Record<string, unknown>): string {
  if (!content || !Array.isArray(content.ops)) {
    return "";
  }

  let html = "";

  for (const op of content.ops as Array<{ insert?: string; attributes?: Record<string, unknown> }>) {
    if (typeof op.insert === "string") {
      let text = op.insert
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br>");

      if (op.attributes) {
        if (op.attributes.bold) {
          text = `<strong>${text}</strong>`;
        }
        if (op.attributes.italic) {
          text = `<em>${text}</em>`;
        }
        if (op.attributes.underline) {
          text = `<u>${text}</u>`;
        }
        if (op.attributes.link) {
          text = `<a href="${op.attributes.link}" target="_blank" rel="noopener noreferrer">${text}</a>`;
        }
      }

      html += text;
    }
  }

  return html;
}
